import { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, Alert, Stack } from '@mui/material';
import RealtimeRefreshControl from '../../components/common/RealtimeRefreshControl';
import RealtimeLineChart from '../../components/naver/chart/RealtimeLineChart';
import apiClient from '../../utils/axiosClient';
import dayjs from 'dayjs';
import { NewsDirectRealtimeStats } from '../../types/naver.types';

export default function NewsDirectRealtimeStatsPage() {
  const [data, setData] = useState<NewsDirectRealtimeStats | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [intervalMinutes, setIntervalMinutes] = useState<number>(5);
  const [lastUpdated, setLastUpdated] = useState<string>('');

  const fetchData = useCallback(() => {
    setLoading(true);
    setErrorMessage('');
    apiClient
      .get('/api/v1/stat/news/direct/realtime')
      .then((res) => {
        setData(res.data);
        setLastUpdated(dayjs().format('HH:mm:ss'));
      })
      .catch(() => setErrorMessage('데이터 로드에 실패했습니다. 잠시 후 다시 시도해주세요.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <Box sx={{ opacity: loading ? 0.4 : 1, transition: 'opacity 0.3s ease', minHeight: '100vh' }}>
      <RealtimeRefreshControl
        intervalMinutes={intervalMinutes}
        onIntervalChange={setIntervalMinutes}
        onRefresh={fetchData}
        lastUpdated={lastUpdated}
      />

      <Box sx={{ mt: 3 }}>
        {loading && !data ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <CircularProgress size={50} sx={{ color: '#0044D2' }} />
          </Box>
        ) : errorMessage ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <Alert severity="error">{errorMessage}</Alert>
          </Box>
        ) : data && (
          <Stack spacing={3}>
            <RealtimeLineChart title="할당 현황" data={data.allocationStats} valueKey="calls" />
            <RealtimeLineChart title="클릭 현황" data={data.clickStats} valueKey="requests" />
            <RealtimeLineChart title="포스트백 현황" data={data.postbackStats} valueKey="postbackSuccess" />
          </Stack>
        )}
      </Box>
    </Box>
  );
}
